import type { SafeArea } from './SystemApi'
import type { RundotGameAPI } from '../rundot-game-api/types'

/**
 * Validates and coerces safe area insets into non-negative finite numbers.
 */
export function sanitizeSafeArea(raw?: Partial<SafeArea> | null): SafeArea {
  return {
    top: typeof raw?.top === 'number' && Number.isFinite(raw.top) && raw.top >= 0 ? raw.top : 0,
    right: typeof raw?.right === 'number' && Number.isFinite(raw.right) && raw.right >= 0 ? raw.right : 0,
    bottom: typeof raw?.bottom === 'number' && Number.isFinite(raw.bottom) && raw.bottom >= 0 ? raw.bottom : 0,
    left: typeof raw?.left === 'number' && Number.isFinite(raw.left) && raw.left >= 0 ? raw.left : 0,
  }
}

/**
 * Injects CSS custom properties on document.documentElement (:root) so web games
 * can style layout with standard CSS variables:
 *   --rundot-safe-area-top
 *   --rundot-safe-area-right
 *   --rundot-safe-area-bottom
 *   --rundot-safe-area-left
 */
export function applySafeAreaCssVariables(safeArea: SafeArea): void {
  if (typeof document !== 'undefined' && document.documentElement?.style) {
    const style = document.documentElement.style
    style.setProperty('--rundot-safe-area-top', `${safeArea.top}px`)
    style.setProperty('--rundot-safe-area-right', `${safeArea.right}px`)
    style.setProperty('--rundot-safe-area-bottom', `${safeArea.bottom}px`)
    style.setProperty('--rundot-safe-area-left', `${safeArea.left}px`)
  }
}

/**
 * Single write path for safe area replica:
 * 1. Validates and sanitizes insets
 * 2. Updates `rundotGameApi._safeAreaData`
 * 3. Injects CSS custom properties
 */
export function applySafeAreaUpdate(
  rundotGameApi: RundotGameAPI,
  raw?: Partial<SafeArea> | null,
): SafeArea {
  const safeArea = sanitizeSafeArea(raw)
  rundotGameApi._safeAreaData = safeArea
  applySafeAreaCssVariables(safeArea)
  return safeArea
}
